/**
 * Sync response message types sent from server to client
 */
export enum SyncResponseMessageType {
  ERROR = "error",
  REGISTERED = "registered",
  UPDATED_ITEMS_COUNT = "updatedItemsCount",
  ITEMS_WITH_REVISIONS = "itemsWithRevisions",
  ITEMS_UPDATED = "itemsUpdated",
  UPDATE_ITEMS_SUCCESS = "updateItemsSuccess",
}

/**
 * Sync message types sent from client to server
 */
export enum SyncMessageType {
  REGISTER = "register",
  GET_UPDATED_ITEMS_COUNT = "getUpdatedItemsCount",
  FETCH_ITEMS_WITH_REVISIONS = "fetchItemsWithRevisions",
  UPDATE_ITEMS = "updateItems",
}

/**
 * Item type enum that mirrors the database schema definition
 */
export enum SyncItemType {
  Tags = "tags",
  Note = "note",
  Task = "task",
  Comment = "comment",
  Habit = "habit",
  UserPreference = "userPreference",
}

/**
 * Core type for a revision
 */
export type SyncRevision = {
  id: string
  itemId: string
  encryptedData: string
  createdAt?: Date | number
}

/**
 * Core type for an item
 */
export type SyncItem = {
  id: string
  type: SyncItemType
  createdAt?: Date | number
  updatedAt?: Date | number
}

/**
 * Item with its revisions
 */
export type SyncItemWithRevisions = SyncItem & {
  revisions: SyncRevision[]
}

// Message data type definitions for client to server messages
export interface SyncRegisterMessage {
  type: SyncMessageType.REGISTER
  jwtToken?: string
}

export interface SyncGetUpdatedItemsCountMessage {
  type: SyncMessageType.GET_UPDATED_ITEMS_COUNT
  timestamp: number
}

export interface SyncFetchItemsWithRevisionsMessage {
  type: SyncMessageType.FETCH_ITEMS_WITH_REVISIONS
  timestamp: number
  offset: number
  limit: number
}

export interface SyncUpdateItemsMessage {
  type: SyncMessageType.UPDATE_ITEMS
  items: Array<{
    item: SyncItem
    revision: SyncRevision
  }>
}

// Union type for all client to server messages
export type SyncMessage =
  | SyncRegisterMessage
  | SyncGetUpdatedItemsCountMessage
  | SyncFetchItemsWithRevisionsMessage
  | SyncUpdateItemsMessage

// Response data type definitions for server to client messages
export interface SyncErrorResponse {
  type: SyncResponseMessageType.ERROR
  data: {
    message: string
  }
}

export interface SyncUpdatedItemsCountResponse {
  type: SyncResponseMessageType.UPDATED_ITEMS_COUNT
  data: {
    count: number
    timestamp: number
  }
}

export interface SyncItemsWithRevisionsResponse {
  type: SyncResponseMessageType.ITEMS_WITH_REVISIONS
  data: {
    items: SyncItemWithRevisions[]
    timestamp: number
    offset: number
    limit: number
    total: number
  }
}

export interface SyncItemsUpdatedResponse {
  type: SyncResponseMessageType.ITEMS_UPDATED
  data: {
    updates: Array<{
      item: SyncItem
      revision: SyncRevision
    }>
  }
}

export interface SyncRegisteredResponse {
  type: SyncResponseMessageType.REGISTERED
  data: {
    userId: string
  }
}

export interface SyncUpdateItemsSuccessResponse {
  type: SyncResponseMessageType.UPDATE_ITEMS_SUCCESS
  data: {
    itemIds: string[]
  }
}

// Union type for all server to client messages
export type SyncResponse =
  | SyncErrorResponse
  | SyncUpdatedItemsCountResponse
  | SyncItemsWithRevisionsResponse
  | SyncItemsUpdatedResponse
  | SyncUpdateItemsSuccessResponse
  | SyncRegisteredResponse

// Type mapping system for messages
export type SyncMessageDataMap = {
  [SyncMessageType.REGISTER]: SyncRegisterMessage
  [SyncMessageType.GET_UPDATED_ITEMS_COUNT]: SyncGetUpdatedItemsCountMessage
  [SyncMessageType.FETCH_ITEMS_WITH_REVISIONS]: SyncFetchItemsWithRevisionsMessage
  [SyncMessageType.UPDATE_ITEMS]: SyncUpdateItemsMessage
}

// Type mapping system for responses
export type SyncResponseDataMap = {
  [SyncResponseMessageType.ERROR]: SyncErrorResponse
  [SyncResponseMessageType.REGISTERED]: SyncRegisteredResponse
  [SyncResponseMessageType.UPDATED_ITEMS_COUNT]: SyncUpdatedItemsCountResponse
  [SyncResponseMessageType.ITEMS_WITH_REVISIONS]: SyncItemsWithRevisionsResponse
  [SyncResponseMessageType.ITEMS_UPDATED]: SyncItemsUpdatedResponse
  [SyncResponseMessageType.UPDATE_ITEMS_SUCCESS]: SyncUpdateItemsSuccessResponse
}
